// Derived once from the displayed graph. All stories use existing, typed edges.
const TITLES = {
  CO_FOUNDED_WITH: "Une cofondation documentée", CO_LED_WITH: "Une codirection documentée",
  CO_AUTHORED_WITH: "Une coécriture documentée", MENTOR_OF: "Un mentorat documenté", PROTEGE_OF: "Un mentorat documenté",
  COLLABORATED_CLOSELY_WITH: "Une collaboration documentée",
};
const RICH = new Set(Object.keys(TITLES));

export function buildSpotlights(graph) {
  const pairs = new Map(), byNode = new Map(), result = [], used = new Set();
  graph.forEachEdge((id, data, source, target) => {
    if (!RICH.has(data.relationType) || !graph.hasNode(source) || !graph.hasNode(target)) return;
    const key = [source, target].sort().join("|");
    if (!pairs.has(key)) pairs.set(key, { id, source, target, type: data.relationType });
  });
  for (const edge of pairs.values()) {
    for (const id of [edge.source, edge.target]) {
      if (!byNode.has(id)) byNode.set(id, []);
      byNode.get(id).push(edge);
    }
  }
  const name = id => graph.getNodeAttribute(id, "label") || id;
  const add = (kind, nodes, edges, title, primaryNodeId = nodes[0]) => {
    if (nodes.length < 2 || nodes.length > 6 || edges.length < 1 || edges.length > 6) return;
    if (nodes.some(id => !graph.hasNode(id)) || edges.some(id => !graph.hasEdge(id))) return;
    const signature = [...nodes].sort().join("|");
    if (used.has(signature)) return;
    used.add(signature);
    result.push({ id: `${kind}:${signature}`, kind, nodeIds: nodes, edgeIds: edges,
      title, subtitle: nodes.map(name).join(kind === "PATH" ? " → " : " — "), primaryNodeId });
  };
  // Stable hash disperses candidates across IDs rather than privileging the
  // first crawl/import batch. This is diversity, not a ranking of people.
  const hash = id => { let value = 2166136261; for (const char of id) value = Math.imul(value ^ char.charCodeAt(0), 16777619); return value >>> 0; };
  const seeds = [...pairs.values()].sort((a, b) => hash(a.id) - hash(b.id) || a.id.localeCompare(b.id));
  for (const edge of seeds) {
    if (result.length >= 160) break;
    if (graph.degree(edge.source) > 100 || graph.degree(edge.target) > 100) continue;
    const bridge = graph.degree(edge.source) >= 2 && graph.degree(edge.target) >= 2 &&
      !graph.neighbors(edge.source).some(id => id !== edge.target && graph.areNeighbors(id, edge.target));
    add(bridge ? "BRIDGE" : "RELATION", [edge.source, edge.target], [edge.id],
      bridge ? "Une relation entre deux voisinages" : TITLES[edge.type]);
    const next = (byNode.get(edge.target) || []).filter(e => e.id !== edge.id && graph.degree(e.target === edge.target ? e.source : e.target) <= 100).slice(0, 8);
    for (const second of next) {
      const third = second.source === edge.target ? second.target : second.source;
      if (third === edge.source) continue;
      const closing = pairs.get([edge.source, third].sort().join("|"));
      if (closing) add("TRIANGLE", [edge.source, edge.target, third], [edge.id, second.id, closing.id], "Trois relations documentées");
      else add("PATH", [edge.source, edge.target, third], [edge.id, second.id], "Un chemin dans le réseau");
      if (result.length >= 160) break;
    }
  }
  for (const [center, edges] of byNode) {
    if (result.length >= 190) break;
    const distinct = new Map();
    for (const edge of edges) distinct.set(edge.source === center ? edge.target : edge.source, edge);
    if (distinct.size < 3 || distinct.size > 12 || graph.degree(center) > 35) continue;
    const selected = [...distinct].slice(0, 3);
    add("EGO", [center, ...selected.map(([id]) => id)], selected.map(([, edge]) => edge.id), "Autour d’une personne", center);
  }
  return result;
}

export function eligibleSpotlight(spotlight, graph, relationType) {
  return spotlight.nodeIds.every(id => graph.hasNode(id)) && spotlight.edgeIds.every(id =>
    graph.hasEdge(id) && (relationType === "ALL" || graph.getEdgeAttribute(id, "relationType") === relationType));
}
